import React from 'react';

export interface SourceNodeFileUploadRef {
  handleFileSelect: () => void;
  handleFileDrop: (e: React.DragEvent) => void;
}
import { IconButton, CircularProgress, Snackbar, Alert, Box } from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { FileWithPreview } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import { FileUploadBadge } from './FileUploadBadge';
import { FilePreviewModal } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FilePreviewModal';
import { useSourceNodeFileUpload, FileValidationError } from './hooks/useSourceNodeFileUpload';
import { fileUploadConfig } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/config/fileUploadConfig';
import styles from './SourceNodeFileUpload.module.scss';

interface SourceNodeFileUploadProps {
  sourceId: number;
  onFilesChange?: (files: FileWithPreview[]) => void;
  className?: string;
  isDragging?: boolean;
}

export const SourceNodeFileUpload = React.forwardRef<
  SourceNodeFileUploadRef,
  SourceNodeFileUploadProps
>(({ sourceId, onFilesChange, className = '', isDragging: externalIsDragging }, ref) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [validationErrors, setValidationErrors] = React.useState<FileValidationError[]>([]);
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);

  const handleValidationErrors = React.useCallback((errors: FileValidationError[]) => {
    setValidationErrors(errors);
    setShowErrorSnackbar(errors.length > 0);
  }, []);

  const {
    files,
    dragHandlers: internalDragHandlers,
    handleFileSelect: internalHandleFileSelect,
    handleFileClick,
    removeFile,
    retryFile,
    previewFile,
    setPreviewFile,
    startUpload,
    isUploading,
    removeAllFilesAndReset,
  } = useSourceNodeFileUpload({
    sourceId,
    onFilesChange,
    onValidationErrors: handleValidationErrors,
  });

  const handleFileSelect = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      internalHandleFileSelect();
    }
  }, [internalHandleFileSelect]);

  const handleFileDrop = React.useCallback(
    (e: React.DragEvent) => {
      internalDragHandlers.onDrop(e);
    },
    [internalDragHandlers],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      handleFileSelect,
      handleFileDrop,
    }),
    [handleFileSelect, handleFileDrop],
  );

  const finalIsDragging = externalIsDragging !== undefined ? externalIsDragging : false;
  const showFilesList = files.length > 0;
  const uploadableCount = files.filter((f) => !f.isBlocked && !f.isOverLimit).length;
  const remainingSlots = Math.max(0, fileUploadConfig.maxFiles - uploadableCount);
  const hasPendingFiles = files.some(
    (f) => f.status === 'pending' && !f.isBlocked && !f.isOverLimit,
  );

  return (
    <>
      {showFilesList && (
        <div className={`${styles.filesListContainer} ${className}`}>
          <div className={styles.filesList}>
            {files.map((file, index) => (
              <FileUploadBadge
                key={file.id}
                file={file}
                index={index}
                onClick={() =>
                  file.status === 'error' ? retryFile(file.id) : handleFileClick(file)
                }
                onRemove={() => removeFile(file.id)}
              />
            ))}
            {hasPendingFiles && (
              <div className={styles.uploadButtonContainer}>
                {isUploading ? (
                  <CircularProgress size={32} className={styles.uploadProgress} thickness={3} />
                ) : (
                  <>
                    <IconButton
                      className={styles.uploadButtonCircle}
                      onClick={startUpload}
                      size="small"
                      disabled={!hasPendingFiles}
                    >
                      <CloudUpload fontSize="small" />
                    </IconButton>
                    <IconButton
                      className={styles.uploadButtonCircle}
                      onClick={removeAllFilesAndReset}
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={styles.fileLimitHint}>
            {`Max ${fileUploadConfig.maxFiles} files${remainingSlots < fileUploadConfig.maxFiles ? ` (${remainingSlots} remaining)` : ''}`}
            {fileUploadConfig.blockedExtensions.length > 0 && (
              <> • Not allowed: {fileUploadConfig.blockedExtensionsDisplay}</>
            )}
          </div>
        </div>
      )}

      {finalIsDragging && files.length === 0 && (
        <div className={`${styles.dropHint} ${className}`}>Drop files here to upload</div>
      )}

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          open={!!previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={8000}
        onClose={() => {
          setShowErrorSnackbar(false);
          setValidationErrors([]);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setShowErrorSnackbar(false);
            setValidationErrors([]);
          }}
          severity="warning"
          icon={<WarningAmberIcon fontSize="medium" />}
          sx={{
            width: '100%',
            maxWidth: '500px',
            fontSize: 'var(--font-size-base)',
            fontWeight: 500,
            '& .MuiAlert-icon': { alignItems: 'center' },
            '& .MuiAlert-message': { padding: '8px 0' },
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid var(--color-warning-300, #fcd34d)',
            backgroundColor: 'var(--color-warning-50, #fffbeb)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {validationErrors.map((error, index) => (
              <Box key={index} sx={{ lineHeight: 1.5 }}>
                {error.fileName ? (
                  <>
                    <strong>{error.fileName}</strong>: {error.message}
                  </>
                ) : (
                  error.message
                )}
              </Box>
            ))}
          </Box>
        </Alert>
      </Snackbar>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            internalDragHandlers.onDrop({
              preventDefault: () => {},
              stopPropagation: () => {},
              dataTransfer: { files: e.target.files },
            } as React.DragEvent);
            e.target.value = '';
          }
        }}
      />
    </>
  );
});

SourceNodeFileUpload.displayName = 'SourceNodeFileUpload';

export { useSourceNodeFileUpload } from './hooks/useSourceNodeFileUpload';
